import { formatInvoiceStatus } from "../../i18n/formatters";
import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';

const STATUS_OPTIONS = [
  { value: '', get label() { return tr("All statuses"); } },
  { value: 'DRAFT', get label() { return tr("Draft"); } },
  { value: 'ISSUED', get label() { return tr("Issued"); } },
  { value: 'PARTIALLY_PAID', get label() { return tr("Partially Paid"); } },
  { value: 'PAID', get label() { return tr("Paid"); } },
  { value: 'OVERDUE', get label() { return tr("Overdue"); } },
  { value: 'VOID', get label() { return tr("Void"); } },
];

function formatCurrency(value, currency) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(getLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getStatusTone(status) {
  switch (status) {
    case 'ISSUED':
      return 'iv-status iv-status--issued';
    case 'PAID':
      return 'iv-status iv-status--paid';
    case 'OVERDUE':
      return 'iv-status iv-status--overdue';
    case 'PARTIALLY_PAID':
      return 'iv-status iv-status--partial';
    case 'VOID':
      return 'iv-status iv-status--void';
    default:
      return 'iv-status iv-status--draft';
  }
}

export default function InvoicesPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const history = useNavigate();
  const [filters, setFilters] = useState({ status: '', clientId: '', search: '' });
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const requestConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      'Content-Type': 'application/json',
    },
  }), [auth.token]);

  const loadClients = useCallback(async () => {
    const body = JSON.stringify({ workspace_id: auth.workspace_id, searchTerm: '' });
    const res = await axios.post('/api/client/list', body, requestConfig);
    setClients(res.data.results || []);
  }, [auth.workspace_id, requestConfig]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ workspace_id: auth.workspace_id });
      const res = await axios.get(`/api/invoice/list?${params.toString()}`, requestConfig);
      setInvoices(res.data.results || []);
    } catch (err) {
      setError('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, requestConfig]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const filteredInvoices = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return invoices.filter((invoice) => {
      if (filters.status && invoice.status !== filters.status) return false;
      if (filters.clientId && String(invoice.client_id) !== String(filters.clientId)) return false;

      const clientName = invoice.client?.company_name || invoice.client?.client_name || '';
      if (!query) return true;

      return [
        invoice.invoice_number,
        invoice.status,
        clientName,
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [filters.clientId, filters.search, filters.status, invoices]);

  const hasActiveFilters = Boolean(filters.status || filters.clientId || filters.search.trim());

  const summary = useMemo(() => {
    const initial = {
      draftCount: 0,
      issuedTotal: 0,
      paidTotal: 0,
      outstandingTotal: 0,
    };
    return invoices.reduce((acc, invoice) => {
      if (invoice.status === 'DRAFT') acc.draftCount += 1;
      if (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID' || invoice.status === 'OVERDUE') {
        acc.issuedTotal += Number(invoice.total || 0);
        acc.outstandingTotal += Number(invoice.balance_due || 0);
      }
      if (invoice.status === 'PAID') {
        acc.paidTotal += Number(invoice.total || 0);
      }
      return acc;
    }, initial);
  }, [invoices]);

  return (
    <div>
      <Sidebar />
      <div className='content-wrap' id='wrapper'>
        <div className='main'>
          <div className='iv-shell'>
            <div className='iv-hero'>
              <div>
                <div className='iv-eyebrow'>{tr("Finance Module")}</div>
                <h1>{tr("Invoices")}</h1>
                <p>{tr("Manage draft, issued, and paid invoices with the same operational clarity as your HR modules.")}</p>
              </div>
              <div className='iv-hero-actions'>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => history('/invoice-settings')}>{tr("Invoice Settings")} </button>
                <button type='button' className='cp-btn cp-btn--primary' onClick={() => history('/invoices/new')}>{tr("Create Invoice")} </button>
              </div>
            </div>

            <div className='iv-summary-grid'>
              <div className='iv-stat-card'>
                <span>{tr("Drafts")}</span>
                <strong>{summary.draftCount}</strong>
                <p>{tr("Invoices still being prepared internally.")}</p>
              </div>
              <div className='iv-stat-card'>
                <span>{tr("Issued")}</span>
                <strong>{formatCurrency(summary.issuedTotal, 'EUR')}</strong>
                <p>{tr("Value already shared with clients.")}</p>
              </div>
              <div className='iv-stat-card'>
                <span>{tr("Paid")}</span>
                <strong>{formatCurrency(summary.paidTotal, 'EUR')}</strong>
                <p>{tr("Closed revenue recognized in the workspace.")}</p>
              </div>
              <div className='iv-stat-card'>
                <span>{tr("Outstanding")}</span>
                <strong>{formatCurrency(summary.outstandingTotal, 'EUR')}</strong>
                <p>{tr("Balance still expected from active invoices.")}</p>
              </div>
            </div>

            <div className='iv-toolbar'>
              <div className='iv-filter-group'>
                <select
                  className='pm-input'
                  value={filters.status}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters((prev) => ({ ...prev, status: value }));
                  }}
                >
                  {STATUS_OPTIONS.filter(Boolean).map((item) => (
                    <option key={item.value || 'all'} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <select
                  className='pm-input'
                  value={filters.clientId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters((prev) => ({ ...prev, clientId: value }));
                  }}
                >
                  <option value=''>{tr("All clients")}</option>
                  {clients.filter(Boolean).map((client) => (
                    <option key={client.id} value={client.id}>{client.company_name || client.client_name}</option>
                  ))}
                </select>
              </div>

              <div className='proj-search-wrap iv-search-wrap'>
                <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                  <circle cx='6' cy='6' r='4.5' stroke='#9ca3af' strokeWidth='1.5' />
                  <path d='M10 10l2.5 2.5' stroke='#9ca3af' strokeWidth='1.5' strokeLinecap='round' />
                </svg>
                <input
                  type='text'
                  className='proj-search-input'
                  placeholder={tr("Search invoices, statuses, or clients")}
                  value={filters.search}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters((prev) => ({ ...prev, search: value }));
                  }}
                />
              </div>
            </div>

            {error && <div className='iv-error'>{error}</div>}

            <div className='iv-table-card'>
              {loading ? (
                <div className='cp-loading-card'>{tr("Loading invoices...")}</div>
              ) : filteredInvoices.length === 0 ? (
                <div className='iv-empty'>
                  <svg width='46' height='46' viewBox='0 0 24 24' fill='none' stroke='#d1d5db' strokeWidth='1.5'>
                    <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                    <polyline points='14 2 14 8 20 8' />
                    <line x1='8' y1='13' x2='16' y2='13' />
                    <line x1='8' y1='17' x2='13' y2='17' />
                  </svg>
                  <h3>{hasActiveFilters ? tr("No matching invoices") : tr("No invoices yet")}</h3>
                  <p>
                    {hasActiveFilters
                      ? tr("Try a different status, client, or search term to find the invoice you need.")
                      : tr("Create your first draft to start a professional billing workflow for this workspace.")}
                  </p>
                  <button
                    type='button'
                    className='cp-btn cp-btn--primary'
                    onClick={() => {
                      if (hasActiveFilters) {
                        setFilters({ status: '', clientId: '', search: '' });
                        return;
                      }
                      history('/invoices/new');
                    }}
                  >
                    {hasActiveFilters ? tr("Clear Filters") : tr("Create Invoice")}
                  </button>
                </div>
              ) : (
                <table className='iv-table'>
                  <thead>
                    <tr>
                      <th>{tr("Invoice")}</th>
                      <th>{tr("Client")}</th>
                      <th>Status</th>
                      <th>{tr("Issue Date")}</th>
                      <th>{tr("Due Date")}</th>
                      <th>{tr("Total")}</th>
                      <th>{tr("Balance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                        <tr key={invoice.id} onClick={() => history(`/invoices/${invoice.id}`)}>
                        <td>
                          <div className='iv-table-primary'>{invoice.invoice_number}</div>
                          <div className='iv-table-secondary'>{tr("innerLineItems", { count: invoice.line_item_count })}</div>
                        </td>
                        <td>
                          <div className='iv-table-primary'>{invoice.client?.company_name || invoice.client?.client_name || tr("No client")}</div>
                          <div className='iv-table-secondary'>{invoice.client?.client_name || tr("Billing profile")}</div>
                        </td>
                        <td><span className={getStatusTone(invoice.status)}>{formatInvoiceStatus(invoice.status)}</span></td>
                        <td>{formatDate(invoice.issue_date)}</td>
                        <td>{formatDate(invoice.due_date)}</td>
                        <td>{formatCurrency(invoice.total, invoice.currency)}</td>
                        <td>{formatCurrency(invoice.balance_due, invoice.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
